import * as React from 'react';
import { useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated } from '@/store/slices/authSlice';
import { MobileHeader } from '@/ui/components/MobileHeader';
import { Footer } from '@/ui/components/Footer';

export interface AppLayoutProps {
  /**
   * Content to render inside the layout
   */
  children: React.ReactNode;
  /**
   * Additional className for the root element
   */
  className?: string;
}

/**
 * AppLayout - Main application layout for authenticated pages
 *
 * Features:
 * - MobileHeader (fixed top, all screens, manages menu internally)
 * - Responsive background (tablet+ only: ≥768px, 100% opacity, visible in margins)
 * - White content container with responsive shadow and rounded corners (centered, max-width on tablet+)
 * - Footer (tablet+ only: ≥768px, static positioning)
 *
 * Container Styling (Figma):
 * - Background: #FFF (white) - all screens
 * - Border radius: 5px - tablet+ only (md:rounded-[0.3125rem])
 * - Box shadow: 1px 1px 3px 0 rgba(0, 0, 0, 0.25) - tablet+ only
 * - Classes: container mx-auto px-8 py-8 md:mt-8 w-full md:max-w-[43.75rem] md:min-h-[85vh]
 *
 * Layout Strategy:
 * - Mobile (< 768px):
 *   - Content fills width with white background
 *   - NO rounded corners (sharp edges)
 *   - NO shadow (flat appearance)
 *   - Natural height
 * - Tablet+ (≥ 768px):
 *   - Content centered with max-width (700px)
 *   - Margin-top (32px) reveals background at top
 *   - Min-height (85vh) for better appearance with minimal content
 *   - Rounded corners (5px border-radius)
 *   - Box shadow creates depth/elevation
 *   - Background image visible in margins (top, left, right, bottom)
 *
 * Usage:
 * - Profile page, Trophy Room, Public Trophy Room, NFC Scan
 * - Any page that needs header + menu + responsive layout
 */
export const AppLayout = React.forwardRef<HTMLDivElement, AppLayoutProps>(
  ({ children }, ref) => {
    const isAuthenticated = useAppSelector(selectIsAuthenticated);

    return (
      <div ref={ref} className="relative min-h-screen flex flex-col">
        {/* Background - tablet+ only, full opacity, visible in margins only */}
        <div
          className="hidden md:block fixed inset-0 bg-[url('/images/app-bg.jpg')] bg-cover bg-center pointer-events-none -z-10"
          aria-hidden="true"
        />

        {/* Header (manages menu internally) */}
        <MobileHeader fixed={true} isAuthenticated={isAuthenticated} />

        {/* Main Content */}
        <main className="flex-1 relative z-10">
          <div className="container mx-auto px-8 py-8 md:mt-8 w-full md:max-w-[43.75rem] md:min-h-[85vh] bg-white md:rounded-[0.3125rem] md:shadow-[1px_1px_3px_0_rgba(0,0,0,0.25)]">
            {children}
          </div>
        </main>

        {/* Footer - tablet+ only, static */}
        <Footer className="hidden md:block relative z-10" />
      </div>
    );
  }
);

AppLayout.displayName = 'AppLayout';
